import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";
import { Link } from "@tanstack/react-router";
import { useId } from "react";

import { cn } from "@/shared/lib";
import { Button } from "@/shared/ui/button";

import { useStartSeries } from "../../api/useStartSeries";
import type { DrillSeries, DrillsOverview } from "../../model/Drill";
import { GRADE_LABELS } from "../../model/GRADE_LABELS";
import { KIND_TITLES } from "../../model/KIND_TITLES";
import { seriesScore } from "../../model/seriesProgress";
import { TOPIC_LABELS } from "../../model/TOPIC_LABELS";
import { TopicGrid } from "./TopicGrid";
import { formatNumber } from "../../model/formatNumber";

const OF_TOTAL = msg({ message: "of {total}" });
const SUMMARY = msg({
  message:
    "{right} right, {close} close, {wrong} wrong. Topics you missed come back tomorrow with another task.",
});
const DAYS = msg({ message: "{days, plural, one {day} other {days}} in a row" });

const GRADE_ICON = {
  right: { icon: "✓", tone: cn("bg-good/15 text-good") },
  close: { icon: "~", tone: cn("bg-warn/15 text-warn") },
  wrong: { icon: "×", tone: cn("bg-bad/15 text-bad") },
} as const;

type SeriesResultProps = { series: DrillSeries; overview: DrillsOverview };

// Итог серии: очки, дни подряд, задачи серии с оценками, темы и что дальше.
export const SeriesResult = ({ series, overview }: SeriesResultProps) => {
  const { i18n, t } = useLingui();
  const headingId = useId();
  const tasksId = useId();
  const start = useStartSeries();
  const score = seriesScore(series.items);
  const total = series.items.length;
  let title = t`Something to work on`;
  if (score.points === total) {
    title = t`Flawless`;
  } else if (score.points * 2 >= total) {
    title = t`Good series`;
  }
  return (
    <div className="flex flex-col gap-5">
      <section
        aria-labelledby={headingId}
        className="flex flex-wrap items-center gap-x-7 gap-y-4 rounded-2xl border border-border-strong bg-surface-raised px-6 py-5"
      >
        <div className="flex size-23 shrink-0 flex-col items-center justify-center rounded-full border-[5px] border-accent">
          <span className="font-mono text-[30px] leading-none font-semibold">
            {formatNumber(score.points, i18n.locale, { maximumFractionDigits: 1 })}
          </span>
          <span className="text-xs text-muted">{i18n._({ ...OF_TOTAL, values: { total } })}</span>
        </div>
        <div className="flex min-w-0 flex-[1_1_340px] flex-col gap-1">
          <h1 id={headingId} className="font-display text-[32px] leading-tight">
            {title}
          </h1>
          <span className="text-sm text-muted">{i18n._({ ...SUMMARY, values: score })}</span>
        </div>
        {overview.streak_days > 0 && (
          <div className="flex flex-col items-center rounded-[10px] border border-border bg-surface px-4 py-2.5">
            <span className="font-mono text-[22px] font-semibold text-accent">
              {overview.streak_days}
            </span>
            <span className="text-xs text-subtle">
              {i18n._({ ...DAYS, values: { days: overview.streak_days } })}
            </span>
          </div>
        )}
      </section>
      <section
        aria-labelledby={tasksId}
        className="flex flex-col gap-1 rounded-xl border border-border bg-surface px-5 py-4"
      >
        <h2 id={tasksId} className="mb-1 text-lg font-semibold">
          {t`Tasks in this series`}
        </h2>
        <ul>
          {series.items.map((item) => {
            const grade = item.attempt?.grade ?? "wrong";
            return (
              <li
                key={item.drill_id}
                className="grid grid-cols-[28px_minmax(0,1fr)_auto] items-center gap-3 border-t border-sidebar-active py-2"
              >
                <span
                  aria-hidden="true"
                  className={cn(
                    "inline-flex size-7 items-center justify-center rounded-full font-mono font-semibold",
                    GRADE_ICON[grade].tone,
                  )}
                >
                  {GRADE_ICON[grade].icon}
                </span>
                <span className="flex min-w-0 flex-col">
                  <span className="font-semibold">{i18n._(KIND_TITLES[item.spec.kind])}</span>
                  <span className="text-[13px] text-subtle">
                    {i18n._(TOPIC_LABELS[item.spec.topic])}
                  </span>
                </span>
                <span className="text-sm text-muted">{i18n._(GRADE_LABELS[grade])}</span>
              </li>
            );
          })}
        </ul>
      </section>
      <TopicGrid topics={overview.topics} />
      <div className="flex flex-wrap items-center gap-2.5">
        {overview.available > 0 ? (
          <Button
            size="lg"
            className="min-h-12 px-6"
            loading={start.isPending}
            onClick={() => {
              start.mutate({});
            }}
          >
            {start.isPending ? t`Building a series…` : t`Another series`}
          </Button>
        ) : (
          <span className="text-sm text-muted">
            {t`That's all for today. New tasks appear after your next analysis.`}
          </span>
        )}
        <Button asChild variant="outline" size="lg" className="min-h-12 no-underline">
          <Link to="/">{t`Analyze another match`}</Link>
        </Button>
      </div>
    </div>
  );
};
